import { isAxiosError, type AxiosError } from "@membentry/shared-api";
import type { ReportabilityKind } from "@membentry/shared-observability";

import { isDeviceOffline } from "@/lib/device-offline";
import { reportWebError } from "@/observability/report-error";

// WHY: the scanner session reports these routes once per terminal failure, after its retries.
const SCANNER_SESSION_OWNED_ROUTES = [
  /\/members\/validate\//,
  /\/members\/record-attendance(\?|$)/,
  /\/clubs\/[^/]+\/reception-snapshot(\?|$)/,
  /\/clubs\/[^/]+\/attendance-events:batch(\?|$)/,
];


function routeTemplate(url?: string): string {
  if (!url) return "unknown";
  return url.split("?")[0].replace(/\/\d+(?=\/|$)/g, "/{id}").replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, "/{id}");
}

function classify(status: number | undefined): ReportabilityKind {
  if (status === undefined) return "infrastructure";
  return status >= 500 ? "operational" : "api-owned";
}

export function reportWebHttpError(error: AxiosError): void {
  if (!isAxiosError(error) || error.code === "ERR_CANCELED") return;
  const url = error.config?.url;
  if (url && SCANNER_SESSION_OWNED_ROUTES.some((route) => route.test(url))) return;
  const status = error.response?.status;
  // WHY: a response-less failure while the device is offline is expected, not a defect.
  if (status === undefined && isDeviceOffline()) return;
  const method = (error.config?.method ?? "?").toUpperCase();
  const route = routeTemplate(url);
  const context = `HTTP ${status ?? "network"} ${method} ${route}`;
  reportWebError({
    error: new Error(context),
    context,
    kind: classify(status),
    httpStatus: status,
    tags: {
      seam: "http.interceptor",
      "http.method": method,
      "http.status_code": status === undefined ? undefined : String(status),
    },
    extra: { url: route },
    fingerprint: ["http", method, route, String(status ?? "network")],
  });
}
